import { defineStore } from "pinia";
import { AddressService } from "~/services/address/address.service";

export const useUserAddressStore = defineStore("userAddress", {
    state: () => ({
        addresses: [] as any[],
        loading: false,
        error: null as string | null,
        addressLoading: {} as Record<string, boolean>,
    }),

    getters: {
        hasAddress: (state) => state.addresses.length > 0,
    },

    actions: {
        // Fetch 
        async fetchAddresses() {
            this.loading = true;
            this.error = null;

            try {
                const { data, error } = await AddressService.getUserAddress();
                if (error) throw error;

                this.addresses = data || [];
            } catch (err: any) {
                this.error = err?.message || "Failed to fetch addresses";
                this.addresses = [];
            } finally {
                this.loading = false;
            }
        },

        // Delete → then refetch
        async deleteAddress(addressId: string) {
            try {
                this.addressLoading[addressId] = true;

                const { error } = await AddressService.deleteUserAddress(addressId);
                if (error) throw error;

                // Always sync from backend
                await this.fetchAddresses();

                return true;
            } catch (err: any) {
                this.error = err?.message || "Delete user address failed";
                return false;
            } finally {
                this.addressLoading[addressId] = false;
            }
        },

        async saveAddress(payload: any) {
            try {
                this.loading = true;
                this.error = null;

                const normalize = (val: any) =>
                    (val || "").toString().trim().toLowerCase();

                const isDuplicate = this.addresses.some((addr: any) => {
                    // 🟡 Ignore same record (edit case)
                    if (payload.id && addr.id === payload.id) return false;

                    return (
                        normalize(addr.door) === normalize(payload.door) &&
                        normalize(addr.building) === normalize(payload.building) &&
                        normalize(addr.locality) === normalize(payload.locality) &&
                        normalize(addr.city) === normalize(payload.city) &&
                        normalize(addr.state) === normalize(payload.state) &&
                        normalize(addr.area_code) === normalize(payload.area_code)
                    );
                });

                if (isDuplicate) {
                    this.error = "Address already exists";
                    return null;
                }

                const { data, error } = await AddressService.addUserAddress(payload);
                if (error) throw error;

                await this.fetchAddresses();

                return data;

            } catch (err: any) {
                this.error = err?.message || "Save user address failed";
                return null;
            } finally {
                this.loading = false;
            }
        },


        reset() {
            this.addresses = [];
            this.error = null;
        },
    },

    persist: {
        storage: piniaPluginPersistedstate.localStorage(),
    },
});